import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Accessibility,
  Type,
  Eye,
  Navigation,
  RotateCcw,
  X,
  MousePointer2,
  ImageOff,
  Link2,
  Heading1,
  Pause,
  Languages,
  BookOpen,
  ListTree,
} from "@/lib/icons";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { useAccessibility, type ColorBlindMode, type ContrastMode } from "@/hooks/useAccessibility";
import { ensureColorBlindFilters } from "@/lib/colorBlindFilters";
import ReadingOverlay from "./a11y/ReadingOverlay";
import PageStructurePanel from "./a11y/PageStructurePanel";

const CONTRAST_OPTIONS: { value: ContrastMode; label: string }[] = [
  { value: "none", label: "Default" },
  { value: "high", label: "High" },
  { value: "dark", label: "Dark" },
  { value: "light", label: "Light" },
  { value: "invert", label: "Invert" },
];

const CB_OPTIONS: { value: ColorBlindMode; label: string }[] = [
  { value: "none", label: "Off" },
  { value: "protanopia", label: "Protanopia" },
  { value: "deuteranopia", label: "Deuteranopia" },
  { value: "tritanopia", label: "Tritanopia" },
  { value: "achromatopsia", label: "Grayscale" },
];

interface ToggleRowProps {
  icon: React.ReactNode;
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  id: string;
}

function ToggleRow({ icon, label, checked, onChange, id }: ToggleRowProps) {
  return (
    <div className="flex items-center justify-between py-2">
      <Label htmlFor={id} className="flex items-center gap-2 cursor-pointer text-sm font-normal">
        <span className="text-muted-foreground">{icon}</span>
        {label}
      </Label>
      <Switch id={id} checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

interface PillGroupProps<T extends string> {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  ariaLabel: string;
}

function PillGroup<T extends string>({ value, options, onChange, ariaLabel }: PillGroupProps<T>) {
  return (
    <div role="radiogroup" aria-label={ariaLabel} className="flex flex-wrap gap-1.5">
      {options.map((opt) => (
        <button
          key={opt.value}
          role="radio"
          aria-checked={value === opt.value}
          onClick={() => onChange(opt.value)}
          className={cn(
            "px-3 py-1.5 text-xs rounded-full border transition-colors",
            value === opt.value
              ? "bg-primary text-primary-foreground border-primary"
              : "bg-background text-foreground border-border hover:bg-accent",
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const { settings, update, reset } = useAccessibility();

  useEffect(() => {
    ensureColorBlindFilters();
  }, []);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      {/* Reading overlays */}
      {settings.readingGuide && <ReadingOverlay mode="guide" />}
      {settings.readingMask && <ReadingOverlay mode="mask" />}

      {/* Floating button */}
      <button
        type="button"
        data-a11y-internal=""
        aria-label="Open accessibility menu"
        aria-expanded={open}
        aria-controls="a11y-panel"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "fixed bottom-6 right-6 z-[9999] h-14 w-14 rounded-full bg-primary text-primary-foreground",
          "shadow-lg hover:scale-105 active:scale-95 transition-transform",
          "flex items-center justify-center focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/40",
        )}
      >
        <Accessibility className="h-7 w-7" />
      </button>

      {/* Backdrop */}
      {open && (
        <div
          data-a11y-internal=""
          aria-hidden="true"
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-[9998] bg-black/40 backdrop-blur-sm animate-in fade-in-0"
        />
      )}

      {/* Panel */}
      <aside
        id="a11y-panel"
        data-a11y-internal=""
        role="dialog"
        aria-modal="true"
        aria-label="Accessibility settings"
        className={cn(
          "fixed top-0 right-0 z-[9999] h-full w-full sm:w-[400px] bg-background border-l border-border shadow-2xl",
          "transition-transform duration-300 ease-out",
          open ? "translate-x-0" : "translate-x-full",
        )}
      >
        <div className="flex items-center justify-between px-5 h-16 border-b border-border">
          <div className="flex items-center gap-2">
            <Accessibility className="h-5 w-5 text-primary" />
            <h2 className="font-semibold text-base">Accessibility</h2>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={reset} className="text-xs">
              <RotateCcw className="h-3.5 w-3.5 mr-1" />
              Reset
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setOpen(false)} aria-label="Close">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <Tabs defaultValue="content" className="flex flex-col h-[calc(100%-4rem)]">
          <TabsList className="grid grid-cols-4 mx-5 mt-4">
            <TabsTrigger value="content" className="text-xs gap-1">
              <Type className="h-3.5 w-3.5" />Content
            </TabsTrigger>
            <TabsTrigger value="color" className="text-xs gap-1">
              <Eye className="h-3.5 w-3.5" />Color
            </TabsTrigger>
            <TabsTrigger value="nav" className="text-xs gap-1">
              <Navigation className="h-3.5 w-3.5" />Nav
            </TabsTrigger>
            <TabsTrigger value="pages" className="text-xs gap-1">
              <ListTree className="h-3.5 w-3.5" />Pages
            </TabsTrigger>
          </TabsList>

          <ScrollArea className="flex-1 px-5 py-4">
            {/* Content */}
            <TabsContent value="content" className="space-y-5 mt-0">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm">Text size</Label>
                  <span className="text-xs text-muted-foreground tabular-nums">{Math.round(settings.textScale * 100)}%</span>
                </div>
                <Slider
                  min={100} max={200} step={10}
                  value={[settings.textScale * 100]}
                  onValueChange={([v]) => update("textScale", v / 100)}
                  aria-label="Text size"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm">Line height</Label>
                  <span className="text-xs text-muted-foreground tabular-nums">{settings.lineHeight.toFixed(1)}</span>
                </div>
                <Slider
                  min={10} max={24} step={1}
                  value={[settings.lineHeight * 10]}
                  onValueChange={([v]) => update("lineHeight", v / 10)}
                  aria-label="Line height"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm">Letter spacing</Label>
                  <span className="text-xs text-muted-foreground tabular-nums">{settings.letterSpacing.toFixed(2)}em</span>
                </div>
                <Slider
                  min={0} max={20} step={1}
                  value={[settings.letterSpacing * 100]}
                  onValueChange={([v]) => update("letterSpacing", v / 100)}
                  aria-label="Letter spacing"
                />
              </div>

              <Separator />

              <div className="space-y-1">
                <ToggleRow id="readable" icon={<Type className="h-4 w-4" />} label="Readable font (Verdana)"
                  checked={settings.readableFont} onChange={(v) => update("readableFont", v)} />
                <ToggleRow id="dyslexic" icon={<Languages className="h-4 w-4" />} label="Dyslexia-friendly font"
                  checked={settings.dyslexicFont} onChange={(v) => update("dyslexicFont", v)} />
                <ToggleRow id="bigcursor" icon={<MousePointer2 className="h-4 w-4" />} label="Bigger cursor"
                  checked={settings.bigCursor} onChange={(v) => update("bigCursor", v)} />
                <ToggleRow id="hideimg" icon={<ImageOff className="h-4 w-4" />} label="Hide images"
                  checked={settings.hideImages} onChange={(v) => update("hideImages", v)} />
                <ToggleRow id="stopanim" icon={<Pause className="h-4 w-4" />} label="Pause animations"
                  checked={settings.stopAnimations} onChange={(v) => update("stopAnimations", v)} />
              </div>
            </TabsContent>

            {/* Color */}
            <TabsContent value="color" className="space-y-5 mt-0">
              <div>
                <Label className="text-sm mb-2 block">Contrast mode</Label>
                <PillGroup
                  value={settings.contrastMode}
                  options={CONTRAST_OPTIONS}
                  onChange={(v) => update("contrastMode", v)}
                  ariaLabel="Contrast mode"
                />
              </div>

              <div>
                <Label className="text-sm mb-2 block">Color blindness</Label>
                <PillGroup
                  value={settings.colorBlindMode}
                  options={CB_OPTIONS}
                  onChange={(v) => update("colorBlindMode", v)}
                  ariaLabel="Color blindness simulation"
                />
              </div>

              <Separator />

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm">Saturation</Label>
                  <span className="text-xs text-muted-foreground tabular-nums">{settings.saturation}%</span>
                </div>
                <Slider min={0} max={200} step={10}
                  value={[settings.saturation]}
                  onValueChange={([v]) => update("saturation", v)}
                  aria-label="Saturation"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm">Brightness</Label>
                  <span className="text-xs text-muted-foreground tabular-nums">{settings.brightness}%</span>
                </div>
                <Slider min={50} max={150} step={5}
                  value={[settings.brightness]}
                  onValueChange={([v]) => update("brightness", v)}
                  aria-label="Brightness"
                />
              </div>
            </TabsContent>

            {/* Navigation */}
            <TabsContent value="nav" className="space-y-1 mt-0">
              <ToggleRow id="rg" icon={<BookOpen className="h-4 w-4" />} label="Reading guide (line)"
                checked={settings.readingGuide} onChange={(v) => update("readingGuide", v)} />
              <ToggleRow id="rm" icon={<BookOpen className="h-4 w-4" />} label="Reading mask (spotlight)"
                checked={settings.readingMask} onChange={(v) => update("readingMask", v)} />
              <ToggleRow id="hl" icon={<Link2 className="h-4 w-4" />} label="Highlight links"
                checked={settings.highlightLinks} onChange={(v) => update("highlightLinks", v)} />
              <ToggleRow id="hh" icon={<Heading1 className="h-4 w-4" />} label="Highlight headings"
                checked={settings.highlightHeadings} onChange={(v) => update("highlightHeadings", v)} />
              <ToggleRow id="fo" icon={<Eye className="h-4 w-4" />} label="Strong focus outline"
                checked={settings.focusOutline} onChange={(v) => update("focusOutline", v)} />
              <ToggleRow id="tt" icon={<Type className="h-4 w-4" />} label="Show tooltips on hover"
                checked={settings.tooltips} onChange={(v) => update("tooltips", v)} />
            </TabsContent>

            {/* Page Structure */}
            <TabsContent value="pages" className="mt-0">
              <PageStructurePanel onJump={() => setOpen(false)} />
            </TabsContent>
          </ScrollArea>

          <div className="border-t border-border px-5 py-3 text-center">
            <Link
              to="/accessibility"
              onClick={() => setOpen(false)}
              className="text-xs text-muted-foreground hover:text-primary underline-offset-4 hover:underline"
            >
              Read our Accessibility Statement
            </Link>
          </div>
        </Tabs>
      </aside>
    </>
  );
}
